import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import type { TicketModels } from "@/api/client/ticket"
import { ticketApi, ticketSchemas } from "@/api/client/ticket"
import type {
  TicketCommentUpdateBody,
  TicketCommentUpdateBodyContactFromCategory,
  TicketCommentUpdateBodyContactToCategory,
  TicketCommentUpdateBodyContactType,
} from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import {
  type ContactCategory,
  ContactPersonSelector,
} from "@/components/ticket/contact-person-selector"
import { ContactTypeSelector } from "@/components/ticket/contact-type-selector"
import { TicketStatusSelector } from "@/components/ticket/ticket-status-selector"
import { Button } from "@/components/ui/button"
import { DateTimePickerField } from "@/components/ui/date-time-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { RequiredBadge } from "@/components/ui/required-badge"
import { Textarea } from "@/components/ui/textarea"

type TicketComment = TicketModels.TicketGet200TicketTicketCommentsItem

interface SupportHistoryEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  comment?: TicketComment
  onSave?: () => void
}

const supportHistoryEditFormSchema = ticketSchemas.ticketCommentUpdateBody
  .omit({
    contactFromOwnerId: true,
    contactFromTenantId: true,
    contactFromAdminId: true,
    contactToOwnerId: true,
    contactToTenantId: true,
    contactToAdminId: true,
  })
  .extend({
    contactType: z.string().min(1, "連絡手段は必須です"),
    status: z.string().min(1, "対応後ステータスは必須です"),
    contactAt: z.string().min(1, "連絡日時は必須です"),
    contactFromCategory: z.string().min(1, "連絡者区分（FROM）は必須です"),
    contactToCategory: z.string().min(1, "連絡者区分（TO）は必須です"),
    content: z.string().min(1, "連絡内容は必須です"),
    contactFromId: z.string().optional(),
    contactToId: z.string().optional(),
    contactFromText: z.string().optional(),
    contactToText: z.string().optional(),
  })

type SupportHistoryEditFormData = z.infer<typeof supportHistoryEditFormSchema>

const toApiPayload = (data: SupportHistoryEditFormData): TicketCommentUpdateBody => {
  const contactFromIds: {
    contactFromOwnerId: string | null
    contactFromTenantId: number | null
    contactFromAdminId: string | null
    contactFromText: string | null
  } = {
    contactFromOwnerId: null,
    contactFromTenantId: null,
    contactFromAdminId: null,
    contactFromText: null,
  }

  if (data.contactFromCategory === "owner" && data.contactFromId) {
    contactFromIds.contactFromOwnerId = data.contactFromId
  } else if (data.contactFromCategory === "tenant" && data.contactFromId) {
    contactFromIds.contactFromTenantId = Number.parseInt(data.contactFromId, 10)
  } else if (data.contactFromCategory === "oc" && data.contactFromId) {
    contactFromIds.contactFromAdminId = data.contactFromId
  }
  contactFromIds.contactFromText = data.contactFromText?.trim() || null

  // TO側のID設定
  const contactToIds: {
    contactToOwnerId: string | null
    contactToTenantId: number | null
    contactToAdminId: string | null
    contactToText: string | null
  } = {
    contactToOwnerId: null,
    contactToTenantId: null,
    contactToAdminId: null,
    contactToText: null,
  }

  if (data.contactToCategory === "owner" && data.contactToId) {
    contactToIds.contactToOwnerId = data.contactToId
  } else if (data.contactToCategory === "tenant" && data.contactToId) {
    contactToIds.contactToTenantId = Number.parseInt(data.contactToId, 10)
  } else if (data.contactToCategory === "oc" && data.contactToId) {
    contactToIds.contactToAdminId = data.contactToId
  }
  contactToIds.contactToText = data.contactToText?.trim() || null

  return {
    status: data.status as TicketComment["status"],
    contactAt: data.contactAt,
    contactType: data.contactType as TicketCommentUpdateBodyContactType,
    contactFromCategory: data.contactFromCategory as TicketCommentUpdateBodyContactFromCategory,
    ...contactFromIds,
    contactToCategory: data.contactToCategory as TicketCommentUpdateBodyContactToCategory,
    ...contactToIds,
    content: data.content,
  }
}

// 連絡者のIDとテキストを抽出するヘルパー関数
function extractContactId(
  comment: TicketComment,
  direction: "from" | "to"
): { id: string; text: string } {
  const prefix = direction === "from" ? "contactFrom" : "contactTo"

  const ownerId = comment[`${prefix}OwnerId` as const]
  const text = comment[`${prefix}Text` as const] ?? ""
  if (ownerId) return { id: ownerId, text }

  const tenantId = comment[`${prefix}TenantId` as const]
  if (tenantId) return { id: String(tenantId), text }

  const adminId = comment[`${prefix}AdminId` as const]
  if (adminId) return { id: adminId, text }
  if (text) return { id: "", text }

  return { id: "", text: "" }
}

function extractSelectedTenant(
  comment: TicketComment | undefined,
  direction: "from" | "to"
): { selectedTenantId?: number; selectedTenantName?: string } {
  if (!comment) return {}

  const tenantId = direction === "from" ? comment.contactFromTenantId : comment.contactToTenantId
  const tenantName =
    direction === "from" ? comment.contactFromTenant?.name : comment.contactToTenant?.name

  if (!tenantId || !tenantName) return {}

  return {
    selectedTenantId: tenantId,
    selectedTenantName: tenantName,
  }
}

export function SupportHistoryEditModal({
  open,
  onOpenChange,
  comment,
  onSave,
}: SupportHistoryEditModalProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SupportHistoryEditFormData>({
    resolver: zodResolver(supportHistoryEditFormSchema),
    defaultValues: {
      contactType: "",
      status: "",
      contactAt: "",
      contactFromCategory: "",
      contactFromId: "",
      contactFromText: "",
      contactToCategory: "",
      contactToId: "",
      contactToText: "",
      content: "",
    },
  })

  const updateMutation = ticketApi.useTicketCommentUpdate({
    mutation: {
      onSuccess: () => {
        onSave?.()
        reset()
        onOpenChange(false)
      },
      onError: (error) => {
        toast.error("対応履歴の更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  // モーダルが開かれた時にコメントデータをフォームにセット
  useEffect(() => {
    if (open && comment) {
      const from = extractContactId(comment, "from")
      const to = extractContactId(comment, "to")

      reset({
        contactType: comment.contactType || "",
        status: comment.status || "",
        contactAt: comment.contactAt || "",
        contactFromCategory: comment.contactFromCategory || "",
        contactFromId: from.id,
        contactFromText: from.text,
        contactToCategory: comment.contactToCategory || "",
        contactToId: to.id,
        contactToText: to.text,
        content: comment.content || "",
      })
    }
  }, [open, comment, reset])

  const handleFormSubmit = (data: SupportHistoryEditFormData) => {
    if (!comment) return

    const payload = toApiPayload(data)
    updateMutation.mutate({
      id: comment.ticketId,
      ticketCommentId: comment.id,
      data: payload,
    })
  }

  const handleCancel = () => {
    reset()
    onOpenChange(false)
  }

  const selectedFromTenant = extractSelectedTenant(comment, "from")
  const selectedToTenant = extractSelectedTenant(comment, "to")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto p-[25px] gap-[20px]">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#3f4652]">対応履歴</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            編集したい対応履歴を入力してください
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-[15px] mt-4">
          {/* 連絡日時 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">連絡日時</p>
              <RequiredBadge />
            </div>
            <Controller
              name="contactAt"
              control={control}
              render={({ field }) => (
                <DateTimePickerField value={field.value} onChange={field.onChange} required />
              )}
            />
            {errors.contactAt && <p className="text-xs text-red-500">{errors.contactAt.message}</p>}
          </div>

          {/* 対応後ステータス */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">対応後ステータス</p>
              <RequiredBadge />
            </div>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TicketStatusSelector
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="ステータスを選択"
                  error={errors.status?.message}
                />
              )}
            />
          </div>

          {/* 連絡手段 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">連絡手段</p>
              <RequiredBadge />
            </div>
            <Controller
              name="contactType"
              control={control}
              render={({ field }) => (
                <ContactTypeSelector
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="連絡手段"
                  error={errors.contactType?.message}
                />
              )}
            />
          </div>

          {/* 連絡者（FROM） */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">連絡者（FROM）</p>
              <RequiredBadge />
            </div>
            <ContactPersonSelector
              direction="from"
              categoryValue={watch("contactFromCategory") as ContactCategory | ""}
              personIdValue={watch("contactFromId") || ""}
              textValue={watch("contactFromText") || ""}
              onCategoryChange={(value) => {
                setValue(
                  "contactFromCategory",
                  value as SupportHistoryEditFormData["contactFromCategory"]
                )
                setValue("contactFromId", "")
                setValue("contactFromText", "")
              }}
              onPersonIdChange={(value) => setValue("contactFromId", value)}
              onTextChange={(value) => setValue("contactFromText", value)}
              categoryError={errors.contactFromCategory?.message}
              includeEmergency
              {...selectedFromTenant}
            />
          </div>

          {/* 連絡者（TO） */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">連絡者（TO）</p>
              <RequiredBadge />
            </div>
            <ContactPersonSelector
              direction="to"
              categoryValue={watch("contactToCategory") as ContactCategory | ""}
              personIdValue={watch("contactToId") || ""}
              textValue={watch("contactToText") || ""}
              onCategoryChange={(value) => {
                setValue(
                  "contactToCategory",
                  value as SupportHistoryEditFormData["contactToCategory"]
                )
                setValue("contactToId", "")
                setValue("contactToText", "")
              }}
              onPersonIdChange={(value) => setValue("contactToId", value)}
              onTextChange={(value) => setValue("contactToText", value)}
              categoryError={errors.contactToCategory?.message}
              includeEmergency
              {...selectedToTenant}
            />
          </div>

          {/* 連絡内容 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold">連絡内容</p>
              <RequiredBadge />
            </div>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  className="min-h-[150px] resize-none border-slate-300 text-[14px] text-[#3f4652]"
                  placeholder="連絡内容を入力してください"
                />
              )}
            />
            {errors.content && <p className="text-xs text-red-500">{errors.content.message}</p>}
          </div>

          <div className="flex justify-end gap-[20px] mt-4">
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-[#354267] hover:bg-[#354267]/90 text-white text-[14px] font-bold h-[32px] px-[16px]"
            >
              {updateMutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              variant="outline"
              className="border-[#354267] text-[#354267] text-[14px] font-bold h-[32px] px-[16px]"
            >
              キャンセル
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
